import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LibraryCategoryService } from '@service/library-category.service';
import { Library, LibraryAndCategories } from 'icreate/common/models/library';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import {
    libraryCategoryNameMaxLen,
    libraryCategoryValidator,
} from 'icreate/common/validators/library-category-validator/library-category-validator';

enum LibraryCategoryInputName {
    name = 'name',
    library = 'library',
}

@Component({
    selector: 'app-library-category-dialog',
    templateUrl: './library-category-dialog.component.html',
    styleUrls: ['./library-category-dialog.component.css'],
    standalone: false,
})
export class LibraryCategoryDialogComponent {
    readonly NAME_INPUT_NAME = LibraryCategoryInputName.name;
    readonly LIBRARY_INPUT_NAME = LibraryCategoryInputName.library;

    readonly NONE_OPTION = 'None';
    readonly NAME_MAX_LEN = libraryCategoryNameMaxLen;

    libraryNameList: string[];
    libraryCategoryName: string = '';
    selectedLibrary: Library = null;
    libraryCategoryList: LibraryCategory[];

    nameError: string = '';

    isLoading: boolean = false;

    libraryError: string = '';
    editMode: boolean = false;
    validationErrors: ValidationErrorMap = {};
    hasErrors: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            libraryList: LibraryAndCategories[];
            library: LibraryAndCategories;
            vessel: Vessel;
            selectedLibraryCategory: LibraryCategory;
        },
        private dialogRef: MatDialogRef<LibraryCategoryDialogComponent>,
        private libraryCategoryService: LibraryCategoryService,
        private translateService: TranslateService
    ) {}

    ngOnInit() {
        this.libraryNameList = this.data.libraryList.map(library => library.name);
        this.libraryCategoryList = this.data.libraryList.reduce((prev, library) => {
            return prev.concat(library.libraryCategories);
        }, []);

        if (this.data.library?.id) {
            this.selectedLibrary = this.data.libraryList.find(library => library.id === this.data.library?.id);
        }

        if (this.data?.selectedLibraryCategory) {
            this.libraryCategoryName = this.data.selectedLibraryCategory.name;
            this.selectedLibrary = this.data.libraryList.find(
                library => library.id === this.data.selectedLibraryCategory?.libraryId
            );

            this.editMode = true;
        }
        this.checkErrors();
    }

    changeLibraryCategoryName(event: string) {
        this.libraryCategoryName = event;
        this.checkErrors();
    }

    disabledCreateButton() {
        return this.hasErrors;
    }

    selectLibrary(event: string) {
        this.selectedLibrary = this.data.libraryList.find(library => library.name === event);

        this.checkErrors();
    }

    checkErrors() {
        const validationData = {
            name: this.libraryCategoryName,
        };

        const errors = getValidationErrors(libraryCategoryValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        const isRepeated = this.libraryCategoryList?.some(category => category?.name === this.libraryCategoryName);

        if (isRepeated) {
            mappedErrors['name'] = {
                message: this.translateService.instant('editor.library.create.error.title.repeat'),
            };
        }

        if (!this.selectedLibrary) {
            mappedErrors['library'] = {
                message: this.translateService.instant('editor.library.dialog.library-select.error.message'),
            };
        }

        this.hasErrors = Object.keys(mappedErrors).length > 0;
        this.validationErrors = this.hasErrors ? mappedErrors : null;
    }

    close() {
        this.dialogRef.close();
    }

    async save() {
        this.isLoading = true;
        try {
            if (this.editMode) {
                await this.libraryCategoryService.update({
                    ...this.data.selectedLibraryCategory,
                    libraryId: this.selectedLibrary.id,
                    shipyardId: this.data.vessel.shipyardId,
                    name: this.libraryCategoryName,
                });
            } else {
                await this.libraryCategoryService.create({
                    libraryId: this.selectedLibrary.id,
                    shipyardId: this.data.vessel.shipyardId,
                    name: this.libraryCategoryName,
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
            this.dialogRef.close();
        }
    }

    public get validatorFunction() {
        return libraryCategoryValidator;
    }
}
